import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

function PagePreview({ file, splitPoints, onToggleSplit }) {
  const [pdf, setPdf] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const arrayBuffer = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (!cancelled) {
          setPdf(doc);
          setTotalPages(doc.numPages);
        }
      } catch (e) {
        console.error('Failed to load PDF:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (file) load();
    return () => { cancelled = true; };
  }, [file]);

  const getGroupIndex = (pageNum) => {
    const sorted = [...splitPoints].sort((a, b) => a - b);
    let group = 0;
    for (const sp of sorted) {
      if (pageNum > sp) group++;
      else break;
    }
    return group;
  };

  const groupColors = [
    '#dbeafe', '#fce7f3', '#d1fae5', '#fef3c7',
    '#e0e7ff', '#fde68a', '#cffafe', '#fecaca',
    '#d9f99d', '#e9d5ff',
  ];

  if (loading) {
    return <div className="preview-loading">Loading PDF...</div>;
  }

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="page-preview">
      <div className="preview-info">
        {totalPages} pages — click the button between pages to set split points
      </div>
      <div className="preview-scroll">
        {pages.map((pageNum) => {
          const groupIdx = getGroupIndex(pageNum);
          const bgColor = groupColors[groupIdx % groupColors.length];
          const isSplitAfter = splitPoints.has(pageNum);
          const isLastPage = pageNum === totalPages;

          return (
            <React.Fragment key={pageNum}>
              <div className="preview-page-full" style={{ backgroundColor: bgColor }}>
                <div className="preview-page-label">
                  Page {pageNum} <span className="preview-part-label">Part {groupIdx + 1}</span>
                </div>
                <PageCanvas pdf={pdf} pageNum={pageNum} />
              </div>
              {!isLastPage && (
                <button
                  className={`preview-split-bar ${isSplitAfter ? 'preview-split-bar--active' : ''}`}
                  onClick={() => onToggleSplit(pageNum)}
                  title={isSplitAfter ? 'Remove split point' : 'Split here'}
                >
                  <div className="split-bar-line" />
                  <span className="split-bar-label">
                    {isSplitAfter ? 'Remove split' : 'Split here'}
                  </span>
                  <div className="split-bar-line" />
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function PageCanvas({ pdf, pageNum }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  const render = useCallback(async () => {
    if (!pdf || !canvasRef.current || !containerRef.current) return;
    try {
      const page = await pdf.getPage(pageNum);
      const containerWidth = containerRef.current.clientWidth || 600;
      const viewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const ctx = canvas.getContext('2d');

      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      setRendered(true);
    } catch (e) {
      console.error(`Failed to render page ${pageNum}:`, e);
    }
  }, [pdf, pageNum]);

  useEffect(() => {
    render();
  }, [render]);

  return (
    <div className="page-canvas-wrap" ref={containerRef}>
      <canvas ref={canvasRef} className="page-canvas" />
      {!rendered && <div className="page-canvas-placeholder">Rendering...</div>}
    </div>
  );
}

export default PagePreview;
